import type { Language } from '@offer-catalog/services/i18n'
import type { Universe, Universes as UniversesType } from '@/types/treeViewTypes'
import { VpIcon } from '@vtmn-play/react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { useUniversePic } from '@/hooks/catalog'
import { ANALYTICS_EVENTS, ANALYTICS_JOURNEY_CHANNELS, trackNavigationEvent } from '@/services/analytics'
import { defineColumnsNumber, defineRowsNumber } from './utils'
import './universes.scss'

function UniverseItem({
  universe,
  currentLanguage,
  onSelect,
  imageUrl,
}: Readonly<{
  universe: Universe
  currentLanguage: Language
  onSelect: (universe: Universe) => void
  imageUrl: string
}>) {
  const getLabel = () => universe.labels[currentLanguage]

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, selectedUniverse: Universe) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect(selectedUniverse)
    }
  }

  return (
    <button
      className={clsx('universe', {
        'universe--no-image': !imageUrl,
      })}
      data-testid={`universe-block_${universe.id}`}
      type="button"
      onClick={() => onSelect(universe)}
      onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) =>
        handleKeyDown(event, universe)}
    >
      <div className="universe-picture-wrapper">
        {imageUrl
          ? (
              <img src={`${imageUrl}?format=auto&f=969x0`} alt="" />
            )
          : <VpIcon name="image" size={48} />}
      </div>
      <span className="universe-label">
        {getLabel()}
      </span>
    </button>
  )
}

export function Universes({
  universes,
}: Readonly<{
  universes: UniversesType
}>) {
  const navigate = useNavigate()
  const { i18n, t } = useTranslation()
  const currentLanguage = i18n.language.split('-')[0] as Language
  const { season } = useParams<{ season: string }>()

  const selectUniverse = async (universe: Universe) => {
    trackNavigationEvent(ANALYTICS_EVENTS.NAV_UNIVERSE_SELECTED, 'hierarchy', {
      journey_channel: ANALYTICS_JOURNEY_CHANNELS.HIERARCHY_NAVIGATION,
      season,
      universe_id: universe.id,
      universe_label: universe.labels[currentLanguage],
      total_universes: universes.length,
    })

    await navigate(`/${season}/universe/${universe.id}`)
  }

  const { data: imageUrls } = useUniversePic(universes.map(universe => universe.id))

  return (
    <div data-testid="universes-container" className="universes-container">
      <h1
        className="universes-title"
        data-testid="universes-title"
        aria-label={`${t('universes')}, ${universes.length}`}
      >
        <span>{t('universes')}</span>
        <span className="universes-count-subtitle" aria-hidden="true">
          {universes.length}
          {' '}
          {t('universes')}
        </span>
      </h1>
      <div
        data-testid="universes"
        className="universes"
        style={
          {
            '--grid-cols': defineColumnsNumber(universes.length),
            '--grid-rows': defineRowsNumber(universes.length),
          } as React.CSSProperties
        }
      >
        {universes.map(universe => (
          <UniverseItem
            key={universe.id}
            universe={universe}
            imageUrl={imageUrls?.[universe.id] ?? ''}
            currentLanguage={currentLanguage}
            onSelect={selectUniverse}
          />
        ))}
      </div>
    </div>
  )
}
